import { Dialog } from '@headlessui/react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { httpsCallable } from 'firebase/functions';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { functions } from '../Firebase';
import Button from '../components/Common/Button';
import Modal, { ModalActionData } from '../components/Common/Modal';
import TextField from '../components/Common/TextField';
import { AuthContext } from '../contexts/AuthContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { classNames, uploadFile } from '../utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

function createSlug(name?: string) {
  if (!name) return undefined;

  return name.trim().replaceAll(/[^a-zA-Z0-9-]{1,}/g, '-');
}

export default function CreateWorkspaceModal({ open, onClose }: Props) {
  const navigate = useNavigate();

  const { show } = useContext(SnackbarContext);
  const { firebaseUser } = useContext(AuthContext);
  const { refetchAccounts } = useContext(UserContext);

  const inputFileRef = useRef<any>();

  const [name, setName] = useState<string>();
  const [slug, setSlug] = useState<string>();
  const [websiteUrl, setWebsiteUrl] = useState<string>();
  const [color, setColor] = useState<string>();
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [logo, setLogo] = useState<string | File>();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();
  const [fileOver, setFileOver] = useState(false);

  useEffect(() => {
    if (open) return;

    setName(undefined);
    setSlug(undefined);
    setWebsiteUrl(undefined);
    setLoadingBrand(false);
    setLogo(undefined);
    setColor(undefined);
    setCreating(false);
  }, [open]);

  const fetchBrandData = useCallback(
    async (websiteUrl: string) => {
      setLoadingBrand(true);
      const fn = httpsCallable<any, any>(functions, 'scrape-websiteData');

      try {
        const data = await fn({
          url: websiteUrl.startsWith('http')
            ? websiteUrl
            : `http://${websiteUrl}`,
        });

        if (data.data?.mainColor) {
          setColor(data.data?.mainColor);
        }
        if (data.data?.mainLogo) {
          setLogo(data.data?.mainLogo);
        }
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        show('Failed to fetch brand data', 'warn');
      }
      setLoadingBrand(false);
    },
    [show],
  );

  const createAccount = useCallback(async () => {
    if (creating) return;

    try {
      if (!logo) {
        throw new Error('Missing logo');
      }
      setCreating(true);

      let logoUrl;
      if (typeof logo === 'string') logoUrl = logo;
      else {
        logoUrl = await uploadFile(
          logo,
          `/accounts/general/${firebaseUser?.uid}_${Date.now()}_${slug}_${
            logo.name
          }`,
        );
      }

      const fn = httpsCallable<any, any>(functions, 'accounts-createAccount');

      await fn({
        name,
        slug,
        websiteUrl: websiteUrl?.startsWith('http')
          ? websiteUrl
          : `http://${websiteUrl}`,
        color,
        logoUrl,
      });
      await refetchAccounts();

      navigate(`/workspace/${slug}`);
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setCreating(false);
  }, [
    creating,
    logo,
    name,
    slug,
    websiteUrl,
    color,
    refetchAccounts,
    navigate,
    firebaseUser?.uid,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: creating,
        onClick: () => onClose(),
      },
      {
        title: 'Create',
        type: 'primary',
        loading: creating,
        onClick: () => createAccount(),
      },
    ],
    [creating, onClose, createAccount],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions}>
      <Dialog.Title
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Create a new workspace
      </Dialog.Title>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          title="Workspace name"
          readOnly={creating}
          value={name}
          onChange={(e) => {
            const newValue = e.target.value;
            const oldSlug = createSlug(name);

            if (!slug || slug == oldSlug) {
              setSlug(createSlug(newValue) || '');
            }

            setName(newValue);
          }}
        />
        <TextField
          title="Slug"
          value={slug}
          readOnly={creating}
          onChange={(e) => setSlug(e.target.value)}
        />
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <TextField
              title="Website"
              placeholder="https://google.com"
              value={websiteUrl}
              readOnly={creating}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </div>
          <div className="mb-0.5">
            <Button
              loading={loadingBrand}
              disabled={creating}
              onClick={() => fetchBrandData(websiteUrl || '')}
            >
              Fetch
            </Button>
          </div>
        </div>
        <div className="flex gap-8">
          <div>
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium leading-6 text-gray-900"
            >
              Color
            </label>
            <input
              type="color"
              value={color}
              readOnly={creating}
              onChange={(e) => setColor(e.target.value)}
              className="h-12"
            />
          </div>
          <div className="w-full">
            <label
              htmlFor="cover-photo"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Logo
            </label>
            <div
              className={classNames(
                fileOver ? 'border-2 border-blue-300' : '',
                'mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-3 py-5',
              )}
              onDragOver={() => setFileOver(true)}
              onDragLeave={() => setFileOver(false)}
              onDrop={() => setFileOver(false)}
              onClick={() => inputFileRef.current?.click()}
            >
              <input
                ref={inputFileRef}
                id="file-upload"
                name="file-upload"
                type="file"
                readOnly={creating}
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  setLogo(file);
                }}
              />
              {logo ? (
                <img
                  src={
                    typeof logo === 'string' ? logo : URL.createObjectURL(logo)
                  }
                />
              ) : (
                <div className="text-center">
                  <PhotoIcon
                    className="mx-auto h-9 w-9 text-gray-300"
                    aria-hidden="true"
                  />
                  <div className="mt-4 flex text-sm leading-6 text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                    >
                      <span>Upload a file</span>
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs leading-5 text-gray-600">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
